import { useEffect, useState } from "react";
import type { ComponentType, ReactNode } from "react";
import { hasAccess, tryGrantFromCode } from "../access/access";
import {
  ArrowRight,
  BadgeCheck,
  BookOpen,
  Briefcase,
  Calculator,
  CheckCircle2,
  ClipboardList,
  Clock3,
  CreditCard,
  DollarSign,
  Download,
  GraduationCap,
  Layers3,
  LockKeyhole,
  PackageCheck,
  Palette,
  PlayCircle,
  Search,
  Settings,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Store,
  Target,
  AlertTriangle,
  Wand2,
  Wrench,
  ScanSearch,
} from "lucide-react";

import artworkPrepImg from "../assets/lock-previews/artwork-prep.png";
import businessBuilderImg from "../assets/lock-previews/business-builder.png";
import costCalculatorImg from "../assets/lock-previews/cost-calculator.png";
import diagnosticCardsImg from "../assets/lock-previews/diagnostic-cards.png";
import glossaryImg from "../assets/lock-previews/glossary.png";
import homepageImg from "../assets/lock-previews/homepage.png";
import howToGuidesImg from "../assets/lock-previews/howto-guides.png";
import interactiveDiagnosisImg from "../assets/lock-previews/interactive-diagnosis.png";
import issuesLibraryImg from "../assets/lock-previews/issues-library.png";
import maintenanceTrackerImg from "../assets/lock-previews/maintenance-tracker.png";
import smartScanImg from "../assets/lock-previews/smart-scan.png";
import skillTestImg from "../assets/lock-previews/skilltest.png";
import vendorsImg from "../assets/lock-previews/vendors.png";
import videoLibraryImg from "../assets/lock-previews/video-library.png";

const PURCHASE_URL = "https://apexdesignsllc.com/product/dtf-wizard-app/";
const TERMS_URL = "https://apexdesignsllc.com/dtf-wizard-terms-of-use/";
const PRIVACY_URL = "https://apexdesignsllc.com/privacy-policy/";
const REFUND_URL = "https://apexdesignsllc.com/refund-policy/";

type PreviewCard = {
  title: string;
  description: string;
  image: string;
  badge: string;
  icon: ComponentType<{ className?: string }>;
};

const previewCards: PreviewCard[] = [
  {
    title: "Home Dashboard",
    description:
      "A clean command center that helps you jump into diagnosis, issues, tools, guides, downloads, and business growth faster.",
    image: homepageImg,
    badge: "Main Hub",
    icon: Sparkles,
  },
  {
    title: "Interactive Diagnosis",
    description:
      "Answer guided questions and narrow down what is actually causing the problem instead of blindly guessing.",
    image: interactiveDiagnosisImg,
    badge: "Guided Fixes",
    icon: Wrench,
  },
  {
    title: "Smart Scan",
    description:
      "Upload a photo of your print issue and get pointed toward likely problems, causes, and next steps faster.",
    image: smartScanImg,
    badge: "Visual Matching",
    icon: ScanSearch,
  },
  {
    title: "Issues Library",
    description:
      "Browse a deep DTF troubleshooting library with symptoms, likely causes, and step-by-step fixes.",
    image: issuesLibraryImg,
    badge: "110+ Issues",
    icon: Search,
  },
  {
    title: "Diagnostic Cards",
    description:
      "Use quick-hit cards when you need likely causes and next moves fast without digging through everything.",
    image: diagnosticCardsImg,
    badge: "84 Cards",
    icon: ClipboardList,
  },
  {
    title: "How-To Guides",
    description:
      "Step-by-step guides for prep, powder, curing, pressing, washing, maintenance, and recovery workflows.",
    image: howToGuidesImg,
    badge: "71 Guides",
    icon: BookOpen,
  },
  {
    title: "Artwork Prep Toolkit",
    description:
      "Includes Photoshop actions, printable CMYK charts, and downloadable shop-ready files that make prep work faster and easier.",
    image: artworkPrepImg,
    badge: "Downloads Included",
    icon: Wand2,
  },
  {
    title: "Color Knockout Tool",
    description:
      "Remove backgrounds, clean edges, and fine-tune artwork with precision controls built for real DTF workflows.",
    image: artworkPrepImg,
    badge: "New Tool",
    icon: Palette,
  },
  {
    title: "Maintenance Tracker",
    description:
      "Track daily, weekly, and monthly maintenance so you stay ahead of clogs, wear, and recurring issues.",
    image: maintenanceTrackerImg,
    badge: "Prevent Problems",
    icon: Settings,
  },
  {
    title: "Cost Calculator",
    description:
      "Price sheets and jobs with better numbers so you stop guessing and protect your margins.",
    image: costCalculatorImg,
    badge: "Profit Tools",
    icon: Calculator,
  },
  {
    title: "Glossary",
    description:
      "Search DTF terms fast and understand the language of the process without confusion.",
    image: glossaryImg,
    badge: "215 Terms",
    icon: Layers3,
  },
  {
    title: "Skill Test",
    description:
      "Test your DTF knowledge, find weak spots, and see where you still need more learning.",
    image: skillTestImg,
    badge: "Level Check",
    icon: GraduationCap,
  },
  {
    title: "Video Library",
    description:
      "Use helpful video walkthroughs when a process is easier to understand visually than by text alone.",
    image: videoLibraryImg,
    badge: "Visual Learning",
    icon: PlayCircle,
  },
  {
    title: "Vendor Directory",
    description:
      "Keep trusted suppliers and shop resources in one place for faster ordering and smoother workflow.",
    image: vendorsImg,
    badge: "Shop Resources",
    icon: Store,
  },
  {
    title: "Business Builder",
    description:
      "Learn how to build, price, manage, market, and scale your DTF business with more confidence.",
    image: businessBuilderImg,
    badge: "Business Growth",
    icon: Briefcase,
  },
];

function BluePanel({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        "relative overflow-hidden rounded-[28px] border border-dtf-accent/20",
        "bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.18),transparent_42%),linear-gradient(180deg,rgba(7,16,35,0.98)_0%,rgba(6,14,30,0.98)_100%)]",
        "shadow-[0_0_0_1px_rgba(245,158,11,0.05),0_0_34px_rgba(37,99,235,0.16)]",
        className,
      ].join(" ")}
    >
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.08),transparent_30%,transparent_70%,rgba(37,99,235,0.06))]" />
      <div className="pointer-events-none absolute inset-0 rounded-[28px] ring-1 ring-inset ring-white/5" />
      {children}
    </div>
  );
}

function SectionHeading({
  icon: Icon,
  eyebrow,
  title,
  description,
}: {
  icon: ComponentType<{ className?: string }>;
  eyebrow: string;
  title: string;
  description?: string;
}) {
  return (
    <div className="mb-6">
      <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-blue">
        <Icon className="h-4 w-4" />
        {eyebrow}
      </div>

      <h2 className="text-2xl font-extrabold tracking-tight text-white md:text-3xl">
        {title}
      </h2>

      {description ? (
        <p className="mt-3 max-w-3xl text-sm leading-6 text-dtf-text-muted md:text-base">
          {description}
        </p>
      ) : null}
    </div>
  );
}

function StatPill({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
      <div className="mb-1 text-xs uppercase tracking-[0.16em] text-dtf-text-muted">
        {label}
      </div>
      <div className="text-base font-semibold text-white">{value}</div>
    </div>
  );
}

function ValueCard({ text }: { text: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white shadow-[0_0_18px_rgba(37,99,235,0.06)]">
      {text}
    </div>
  );
}

function FeatureCard({
  icon: Icon,
  title,
  description,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  description: string;
}) {
  return (
    <div className="rounded-[24px] border border-white/10 bg-black/20 p-5 shadow-[0_0_18px_rgba(37,99,235,0.08)]">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-xl border border-dtf-blue/20 bg-dtf-blue/10 p-2 shadow-[0_0_12px_rgba(59,130,246,0.18)]">
          <Icon className="h-4 w-4 text-dtf-blue" />
        </div>

        <div>
          <h3 className="text-sm font-bold text-white">{title}</h3>
          <p className="mt-2 text-sm leading-6 text-dtf-text-muted">{description}</p>
        </div>
      </div>
    </div>
  );
}

function PreviewCardItem({ card }: { card: PreviewCard }) {
  const Icon = card.icon;

  return (
    <article className="group relative overflow-hidden rounded-[26px] border border-white/10 bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.14),transparent_48%),linear-gradient(180deg,rgba(9,21,44,0.98)_0%,rgba(7,15,31,0.98)_100%)] transition duration-300 hover:-translate-y-1 hover:border-dtf-blue/30 hover:shadow-[0_0_26px_rgba(37,99,235,0.18)]">
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.06),transparent_35%,transparent_65%,rgba(37,99,235,0.04))]" />

      <div className="relative aspect-[16/10] overflow-hidden border-b border-white/10 bg-black">
        <img
          src={card.image}
          alt={card.title}
          className="h-full w-full object-cover object-top transition duration-300 group-hover:scale-[1.02]"
        />

        <div className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full border border-dtf-blue/20 bg-[#08111f]/85 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-dtf-blue backdrop-blur">
          <Icon className="h-3.5 w-3.5" />
          {card.badge}
        </div>
      </div>

      <div className="relative p-5">
        <h3 className="text-xl font-bold tracking-tight text-white">{card.title}</h3>
        <p className="mt-2 text-sm leading-6 text-dtf-text">{card.description}</p>
      </div>
    </article>
  );
}

function BulletItem({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-dtf-accent" />
      <span className="text-sm leading-6 text-white/90">{text}</span>
    </div>
  );
}

export function AccessGate({ children }: { children: ReactNode }) {
  const [code, setCode] = useState("");
  const [, setRefresh] = useState(0);
  const [loading, setLoading] = useState(false);
  const [autoTried, setAutoTried] = useState(false);
  const [error, setError] = useState("");
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    async function tryCodeFromUrl() {
      if (hasAccess()) return;

      const params = new URLSearchParams(window.location.search);
      const codeFromUrl = params.get("code");

      if (!codeFromUrl) {
        setAutoTried(true);
        return;
      }

      setLoading(true);
      setError("");

      const ok = await tryGrantFromCode(codeFromUrl);

      setLoading(false);
      setAutoTried(true);

      if (ok) {
        const cleanUrl =
          window.location.origin +
          window.location.pathname +
          window.location.hash;

        window.history.replaceState({}, document.title, cleanUrl);
        setRefresh((r) => r + 1);
      } else {
        setError("That unlock code could not be verified.");
      }
    }

    void tryCodeFromUrl();
  }, []);

  async function handleUnlock() {
    if (!agreed) {
      setError(
        "Please agree to the Terms of Use, Privacy Policy, and Refund Policy before continuing."
      );
      return;
    }

    setLoading(true);
    setError("");

    const ok = await tryGrantFromCode(code);

    setLoading(false);

    if (ok) {
      setRefresh((r) => r + 1);
    } else {
      setError("Invalid code. Purchase access below.");
    }
  }

  if (hasAccess()) return <>{children}</>;

  return (
    <div className="min-h-screen bg-[#070b14] text-white">
      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
        <BluePanel className="p-6 md:p-10">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_center,rgba(59,130,246,0.24),transparent_45%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_28%)]" />

          <div className="relative grid gap-8 xl:grid-cols-[1.1fr_0.9fr] xl:items-start">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-dtf-blue">
                <LockKeyhole className="h-4 w-4" />
                Premium DTF Troubleshooting System
              </div>

              <h1 className="mt-5 text-4xl font-black tracking-tight text-white md:text-5xl xl:text-6xl">
                Stop guessing. Start fixing your DTF prints faster.
              </h1>

              <p className="mt-5 max-w-3xl text-sm leading-7 text-dtf-text md:text-base">
                DTF Wizard gives you a structured way to troubleshoot issues,
                learn what is actually going wrong, and make better decisions
                without bouncing between random posts, bad advice, and expensive
                trial and error.
              </p>

              <div className="mt-5 rounded-2xl border border-dtf-blue/20 bg-dtf-blue/10 px-5 py-4">
                <div className="flex items-start gap-3">
                  <div className="rounded-xl border border-dtf-blue/25 bg-black/20 p-2">
                    <ScanSearch className="h-4 w-4 text-dtf-blue" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-dtf-blue">
                      Smart Scan
                    </p>
                    <p className="mt-2 text-sm font-semibold leading-6 text-white/90">
                      Upload an image and get pointed toward likely issues,
                      causes, and next steps faster.
                    </p>
                    <p className="mt-1 text-sm leading-6 text-dtf-text-muted">
                      This helps turn random troubleshooting into a more guided,
                      focused process.
                    </p>
                  </div>
                </div>
              </div>

              <p className="mt-5 max-w-2xl text-base font-semibold leading-7 text-white/95">
                If your prints are failing and you do not know why, this was built
                for exactly that moment.
              </p>

              <div className="mt-5 grid gap-3 md:grid-cols-2">
                <ValueCard text="Diagnose print issues step-by-step" />
                <ValueCard text="Reduce wasted film, ink, powder, and time" />
                <ValueCard text="Learn while you fix the problem" />
                <ValueCard text="Use business and pricing tools in one place" />
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href={PURCHASE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-dtf-accent px-6 py-3 text-sm font-bold text-black shadow-[0_0_20px_rgba(245,158,11,0.25)] transition hover:opacity-90"
                >
                  Buy Access
                  <ArrowRight className="h-4 w-4" />
                </a>

                <a
                  href="#inside-the-app"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-black/20 px-6 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:border-dtf-blue/30 hover:bg-black/30"
                >
                  See What Is Inside
                </a>
              </div>

              <div className="mt-6 rounded-2xl border border-dtf-accent/20 bg-dtf-accent/10 px-5 py-4">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-dtf-accent">
                  Real Talk
                </p>
                <p className="mt-2 text-sm font-semibold leading-6 text-white/90">
                  DTF Wizard is not a magic fix. It is a structured system built to
                  reduce guesswork and improve consistency.
                </p>
              </div>

              <p className="mt-4 text-sm font-medium text-dtf-text-muted">
                Based on real-world DTF problems, not guesswork or recycled advice.
              </p>
            </div>

            <div id="unlock-hero" className="grid gap-5">
              <BluePanel className="p-6">
                <SectionHeading
                  icon={LockKeyhole}
                  eyebrow="Unlock"
                  title="Already purchased? Enter your code below."
                  description="Use your unlock code to access the full DTF Wizard experience."
                />

                <div className="flex flex-col gap-3">
                  <input
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className="w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-white outline-none transition placeholder:text-dtf-text-muted focus:border-dtf-blue/35"
                    placeholder="Enter unlock code"
                  />

                  <button
                    disabled={loading || !agreed}
                    onClick={handleUnlock}
                    className="rounded-2xl border border-dtf-blue/35 bg-dtf-blue/12 px-6 py-3 font-semibold text-white transition hover:border-dtf-blue/60 hover:bg-dtf-blue/18 disabled:opacity-60"
                  >
                    {loading ? "Checking..." : "Unlock"}
                  </button>
                </div>

                <label className="mt-4 flex items-start gap-3 text-sm text-dtf-text-muted">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-1 h-4 w-4 accent-yellow-500"
                  />
                  <span className="leading-relaxed">
                    I have read and agree to the{" "}
                    <a
                      href={TERMS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-dtf-accent underline"
                    >
                      Terms of Use
                    </a>
                    ,{" "}
                    <a
                      href={PRIVACY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-dtf-accent underline"
                    >
                      Privacy Policy
                    </a>
                    , and{" "}
                    <a
                      href={REFUND_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-dtf-accent underline"
                    >
                      Refund Policy
                    </a>
                    .
                  </span>
                </label>

                {error && (
                  <div className="mt-4 rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                    {error}
                  </div>
                )}

                {!autoTried && loading && (
                  <p className="mt-3 text-sm text-dtf-text-muted">
                    Checking NFC code...
                  </p>
                )}
              </BluePanel>

              <BluePanel className="p-6">
                <div className="inline-flex items-center gap-2 rounded-full border border-dtf-accent/25 bg-dtf-accent/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-accent">
                  <DollarSign className="h-4 w-4" />
                  Launch Price
                </div>

                <div className="mt-4 flex items-end gap-2">
                  <span className="text-5xl font-black tracking-tight text-white">
                    $39
                  </span>
                  <span className="pb-1 text-sm font-medium text-dtf-text-muted">
                    one-time
                  </span>
                </div>

                <p className="mt-3 text-sm font-semibold text-dtf-accent">
                  No subscription. Full access. Future updates included.
                </p>

                <div className="mt-5 grid gap-3">
                  <BulletItem text="Interactive diagnosis system" />
                  <BulletItem text="Smart Scan and issue matching" />
                  <BulletItem text="Issues library, guides, videos, and tools" />
                  <BulletItem text="Business Builder, calculator, and downloads" />
                  <BulletItem text="Digital access worldwide" />
                  <BulletItem text="U.S. orders include NFC access card" />
                </div>

                <div className="mt-5 rounded-2xl border border-white/10 bg-black/20 px-4 py-4">
                  <div className="text-xs uppercase tracking-[0.16em] text-dtf-text-muted">
                    Why it makes sense
                  </div>
                  <div className="mt-2 text-sm leading-6 text-white/90">
                    Most users waste more than this in their first few mistakes.
                    Fix one issue and this can pay for itself fast.
                  </div>
                </div>

                <div className="mt-5">
                  <a
                    href={PURCHASE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-dtf-accent px-6 py-3 font-bold text-base text-black shadow-[0_0_20px_rgba(245,158,11,0.25)] transition hover:opacity-90"
                  >
                    Buy Access
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
              </BluePanel>
            </div>
          </div>
        </BluePanel>

        <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          <FeatureCard
            icon={Wrench}
            title="Fix problems faster"
            description="Use guided diagnosis and structured troubleshooting instead of wasting more film, ink, powder, and time."
          />
          <FeatureCard
            icon={GraduationCap}
            title="Learn while you work"
            description="Build real understanding with the Glossary, Skill Test, and step-by-step How-To library."
          />
          <FeatureCard
            icon={Calculator}
            title="Protect your margins"
            description="Use pricing and business tools to stop guessing what your jobs should actually cost."
          />
          <FeatureCard
            icon={Briefcase}
            title="Grow smarter"
            description="Use Business Builder to improve your workflow, systems, and decision-making as you scale."
          />
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[1.02fr_0.98fr]">
          <BluePanel className="p-6 md:p-7">
            <SectionHeading
              icon={DollarSign}
              eyebrow="Pricing"
              title="One-time access. No subscription."
              description="Get full access to DTF Wizard with a single payment and use the full troubleshooting and learning system from anywhere."
            />

            <div className="rounded-[28px] border border-dtf-accent/25 bg-[linear-gradient(180deg,rgba(245,158,11,0.10)_0%,rgba(0,0,0,0.14)_100%)] p-5 md:p-6 shadow-[0_0_24px_rgba(245,158,11,0.08)]">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-dtf-accent/25 bg-dtf-accent/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-dtf-accent">
                    Launch Price
                  </div>

                  <div className="mt-4 flex items-end gap-2">
                    <span className="text-5xl font-black tracking-tight text-white">
                      $39
                    </span>
                    <span className="pb-1 text-sm font-medium text-dtf-text-muted">
                      one-time
                    </span>
                  </div>

                  <p className="mt-2 text-sm font-semibold text-dtf-accent">
                    Launch price — will increase as new features are added.
                  </p>

                  <p className="mt-3 max-w-xl text-sm leading-6 text-dtf-text-muted">
                    Full access to troubleshooting tools, business tools, guides,
                    learning resources, downloadable prep tools, and digital access
                    worldwide.
                  </p>
                </div>

                <div className="min-w-[220px] rounded-2xl border border-white/10 bg-black/20 px-4 py-4">
                  <div className="text-xs uppercase tracking-[0.16em] text-dtf-text-muted">
                    Simple math
                  </div>
                  <div className="mt-2 text-sm leading-6 text-white/90">
                    One decent troubleshooting save can cover this cost fast.
                  </div>
                </div>
              </div>

              <div className="mt-5 rounded-2xl border border-dtf-accent/20 bg-black/20 px-4 py-4">
                <div className="flex items-start gap-3">
                  <div className="rounded-xl border border-dtf-accent/20 bg-dtf-accent/10 p-2">
                    <PackageCheck className="h-4 w-4 text-dtf-accent" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">
                      U.S. orders include NFC card
                    </p>
                    <p className="mt-1 text-sm leading-6 text-dtf-text-muted">
                      U.S. customers receive a premium NFC access card at no extra
                      charge. International customers receive digital access only.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 grid gap-3 md:grid-cols-2">
                <BulletItem text="Full DTF Wizard access worldwide" />
                <BulletItem text="Interactive diagnosis system" />
                <BulletItem text="Issues library and diagnostic cards" />
                <BulletItem text="How-To guides and video library" />
                <BulletItem text="Glossary and Skill Test" />
                <BulletItem text="Business Builder and pricing tools" />
                <BulletItem text="Artwork prep downloads and printable charts" />
                <BulletItem text="Color Knockout tool for cleaner artwork prep" />
                <BulletItem text="U.S. orders include NFC access card" />
              </div>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <a
                  href={PURCHASE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-dtf-accent px-6 py-3 font-bold text-black shadow-[0_0_20px_rgba(245,158,11,0.25)] transition hover:opacity-90"
                >
                  Buy Access
                  <ArrowRight className="h-4 w-4" />
                </a>

                <a
                  href="#unlock-hero"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-black/20 px-6 py-3 font-semibold text-white transition hover:-translate-y-0.5 hover:border-dtf-blue/30 hover:bg-black/30"
                >
                  Already Have a Code?
                </a>
              </div>
            </div>

            <div className="mt-6 grid gap-3 md:grid-cols-4">
              <StatPill label="Access Type" value="One-Time Purchase" />
              <StatPill label="Delivery" value="Code By Email" />
              <StatPill label="NFC Card" value="U.S. Only" />
              <StatPill label="Updates" value="Included" />
            </div>
          </BluePanel>

          <BluePanel className="p-6 md:p-7">
            <SectionHeading
              icon={Target}
              eyebrow="Why Buy"
              title="Built to reduce waste and speed up learning"
              description="DTF Wizard is designed to help you make better troubleshooting decisions faster so you waste less and learn more."
            />

            <div className="grid gap-4">
              <FeatureCard
                icon={AlertTriangle}
                title="Stop wasting money guessing"
                description="Bad troubleshooting costs film, ink, powder, garments, time, and confidence. This gives you structure when things go wrong."
              />
              <FeatureCard
                icon={Clock3}
                title="Save time every week"
                description="Instead of searching through scattered videos and random comments, use one organized system built for real DTF workflows."
              />
              <FeatureCard
                icon={ShieldCheck}
                title="Build stronger consistency"
                description="Better maintenance, better troubleshooting, and better habits lead to better results and less chaos."
              />
            </div>

            <div className="mt-6 rounded-2xl border border-dtf-accent/20 bg-dtf-accent/10 px-5 py-4">
              <div className="flex items-start gap-3">
                <div className="rounded-xl border border-dtf-accent/20 bg-black/20 p-2">
                  <Smartphone className="h-4 w-4 text-dtf-accent" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-dtf-accent">
                    Worldwide Access
                  </p>
                  <p className="mt-2 text-sm leading-6 text-white/90">
                    DTF Wizard access is available worldwide. U.S. customers
                    receive an NFC card by mail. International customers receive
                    digital access only by email.
                  </p>
                </div>
              </div>
            </div>
          </BluePanel>
        </div>

        <div id="inside-the-app" className="mt-10">
          <SectionHeading
            icon={Sparkles}
            eyebrow="Inside The App"
            title="See what you unlock"
            description="DTF Wizard brings troubleshooting, learning, shop tools, downloads, and business guidance together in one place."
          />

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {previewCards.map((card) => (
              <PreviewCardItem key={card.title} card={card} />
            ))}
          </div>
        </div>

        <div className="mt-8">
          <BluePanel className="p-6 md:p-7">
            <SectionHeading
              icon={Wand2}
              eyebrow="Artwork Prep Downloads"
              title="High-value downloads are included"
              description="DTF Wizard includes useful downloadable resources that save time, improve prep workflow, and add even more value beyond troubleshooting."
            />

            <div className="grid gap-4 lg:grid-cols-[0.95fr_1.05fr]">
              <div className="relative overflow-hidden rounded-[26px] border border-white/10 bg-black/20">
                <img
                  src={artworkPrepImg}
                  alt="Artwork Prep Toolkit preview"
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <div>
                <div className="grid gap-3 md:grid-cols-2">
                  <BulletItem text="Photoshop halftoning actions" />
                  <BulletItem text="Color knockout actions" />
                  <BulletItem text="Artwork prep workflow actions" />
                  <BulletItem text="Printable CMYK color chart" />
                  <BulletItem text="Free STL film guide files" />
                  <BulletItem text="Damper holder STL file" />
                  <BulletItem text="Powder keeper STL files" />
                  <BulletItem text="Shop-ready downloadable resources" />
                </div>

                <div className="mt-5 rounded-2xl border border-dtf-accent/20 bg-dtf-accent/10 px-5 py-4">
                  <div className="flex items-start gap-3">
                    <div className="rounded-xl border border-dtf-accent/20 bg-black/20 p-2">
                      <Palette className="h-4 w-4 text-dtf-accent" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">
                        More than just troubleshooting
                      </p>
                      <p className="mt-1 text-sm leading-6 text-dtf-text-muted">
                        Buyers are getting troubleshooting tools, learning
                        resources, business support, and useful production
                        downloads all in one place.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 rounded-2xl border border-white/10 bg-black/20 px-5 py-4">
                  <p className="text-sm leading-6 text-white/90">
                    This makes the offer bigger than just fixing a print issue.
                    It supports actual workflow, prep, and shop convenience too.
                  </p>
                </div>
              </div>
            </div>
          </BluePanel>
        </div>

        <div className="mt-8">
          <BluePanel className="p-6 md:p-7">
            <SectionHeading
              icon={CreditCard}
              eyebrow="NFC Access Card"
              title="U.S. customers receive a premium tap-to-access card"
              description="DTF Wizard includes a physical NFC card for U.S. customers at no extra cost, giving you a fast, convenient way to open the app right from your shop."
            />

            <div className="grid gap-4 md:grid-cols-3">
              <FeatureCard
                icon={Smartphone}
                title="Tap for fast access"
                description="Use your phone and tap the card for quick entry when you need troubleshooting help fast."
              />
              <FeatureCard
                icon={PackageCheck}
                title="Included for U.S. orders"
                description="U.S. customers get the NFC card as part of the offer at no extra charge."
              />
              <FeatureCard
                icon={ShieldCheck}
                title="International stays digital"
                description="International customers still get full app access, but no physical NFC card is shipped."
              />
            </div>
          </BluePanel>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-[1fr_auto]">
          <BluePanel className="p-6">
            <SectionHeading
              icon={ShieldCheck}
              eyebrow="Why It Helps"
              title="Built to reduce waste and improve consistency"
              description="When you are stuck, random guessing usually makes things worse. DTF Wizard helps you slow down, think clearer, and troubleshoot with more structure."
            />

            <div className="grid gap-3 md:grid-cols-2">
              <ValueCard text="Reduce wasted film, powder, ink, and garments" />
              <ValueCard text="Troubleshoot with more confidence and less chaos" />
              <ValueCard text="Learn real process knowledge while solving problems" />
              <ValueCard text="Build stronger workflow habits over time" />
            </div>
          </BluePanel>

          <BluePanel className="p-6 lg:min-w-[320px]">
            <div className="flex h-full flex-col justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-blue">
                  <Download className="h-4 w-4" />
                  Get Access
                </div>

                <h3 className="mt-4 text-2xl font-extrabold tracking-tight text-white">
                  Ready to stop guessing?
                </h3>

                <p className="mt-3 text-sm leading-6 text-dtf-text-muted">
                  Buy access now and unlock the full DTF Wizard system, shop
                  downloads, and worldwide digital access.
                </p>
              </div>

              <div className="mt-6 flex flex-col gap-3">
                <a
                  href={PURCHASE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-dtf-accent px-6 py-3 font-bold text-black shadow-[0_0_20px_rgba(245,158,11,0.25)] transition hover:opacity-90"
                >
                  Buy Access
                  <ArrowRight className="h-4 w-4" />
                </a>

                <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-center text-xs leading-6 text-dtf-text-muted">
                  One-time payment. Digital access worldwide. U.S. orders include
                  NFC card. No subscription.
                </div>
              </div>
            </div>
          </BluePanel>
        </div>

        <div className="mt-8 rounded-2xl border border-white/10 bg-black/20 px-5 py-4 text-center text-xs leading-6 text-dtf-text-muted">
          By purchasing and using DTF Wizard, you agree to the{" "}
          <a
            href={TERMS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-dtf-accent underline"
          >
            Terms of Use
          </a>
          ,{" "}
          <a
            href={PRIVACY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-dtf-accent underline"
          >
            Privacy Policy
          </a>
          , and{" "}
          <a
            href={REFUND_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-dtf-accent underline"
          >
            Refund Policy
          </a>
          .
        </div>
      </div>
    </div>
  );
}